import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ProBadge from "@/components/ProBadge";

/**
 * Account card — shows who's signed in, an Admin panel for the site owner
 * (View as New User shadow mode + Reset demo data), and a Sign out button.
 */
export default function AccountPanel() {
  const { user, refresh, logout } = useAuth();
  const sub = useSubscription();
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const isAdmin = user.role === "admin";
  const shadowOn = !!user.shadow_mode;

  const toggleShadow = async () => {
    setBusy(true);
    try {
      await endpoints.adminShadowMode(!shadowOn);
      await refresh();
      toast.success(!shadowOn ? "Viewing as a new user." : "Back to your account.");
      // Land on / so the reload isn't under a fresh onboarding dialog.
      setTimeout(() => window.location.assign("/"), 250);
    } catch {
      toast.error("Couldn't toggle shadow mode.");
    } finally {
      setBusy(false);
    }
  };

  const resetDemo = async () => {
    if (!window.confirm("Wipe everything created inside the 'View as New User' shadow bucket? Your real admin data is untouched.")) return;
    setBusy(true);
    try {
      await endpoints.adminResetDemoData();
      toast.success("Shadow bucket cleared.");
      if (shadowOn) setTimeout(() => window.location.reload(), 250);
    } catch {
      toast.error("Couldn't reset demo data.");
    } finally {
      setBusy(false);
    }
  };

  const migrateLegacy = async () => {
    setBusy(true);
    try {
      const { data } = await endpoints.adminMigrateLegacy();
      const moved = Object.entries(data?.totals || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "nothing to migrate";
      toast.success(`Legacy migration complete. ${moved}`);
    } catch {
      toast.error("Migration failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="account-section"
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
        Account
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="w-14 h-14 rounded-full border hairline"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-full border hairline flex items-center justify-center font-display text-xl bg-[hsl(var(--sand))]/50"
          >
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl" data-testid="account-name">{user.name || "Wanderer"}</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="account-email">{user.email}</div>
          <div className="text-[11px] uppercase tracking-[0.2em] mt-1 flex items-center gap-2" data-testid="account-role">
            <span>{isAdmin ? "Admin" : "Member"}</span>
            {sub.tier === "pro" && <ProBadge testid="account-pro-badge" />}
            {shadowOn && (
              <span className="text-[hsl(var(--terracotta))]">· viewing as new user</span>
            )}
            {sub.preview_override && (
              <span data-testid="account-preview-tier" className="text-[hsl(var(--terracotta))]">
                · previewing {sub.preview_override}
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={logout}
          data-testid="btn-logout"
        >
          Sign out
        </Button>
      </div>

      {isAdmin && (
        <div className="mt-6 pt-6 border-t hairline space-y-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            Admin controls
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-lg">View as New User</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                See the blank-slate onboarding without touching your admin data. Toggle off anytime to return.
              </p>
            </div>
            <button
              type="button"
              onClick={toggleShadow}
              disabled={busy}
              data-testid="btn-shadow-toggle"
              aria-pressed={shadowOn}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline tab-link ${
                shadowOn
                  ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] border-transparent"
                  : "hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {shadowOn ? "Shadow on" : "Shadow off"}
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-lg">Reset Demo Data</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Clears anything created inside the shadow bucket. Your real admin data is left alone.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={resetDemo}
              disabled={busy}
              data-testid="btn-reset-demo"
            >
              Reset demo data
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-lg">Migrate Legacy Data</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Sweeps any documents still stamped <code>default_user</code> under this admin account. Idempotent.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={migrateLegacy}
              disabled={busy}
              data-testid="btn-migrate-legacy"
            >
              Run migration
            </Button>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-lg flex items-center gap-2">
                Preview subscription tier
                <ProBadge testid="admin-preview-tier-badge" />
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                See exactly what a Free or Pro member sees — session-only, does not touch billing.
              </p>
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-full border hairline bg-[hsl(var(--sand))]/40" role="tablist" aria-label="Preview tier">
              {[
                { key: null, label: "Real" },
                { key: "free", label: "As Free" },
                { key: "pro", label: "As Pro" },
              ].map((opt) => {
                const on = (sub.preview_override || null) === opt.key;
                return (
                  <button
                    key={String(opt.key)}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await sub.previewTier(opt.key);
                        toast.success(opt.key ? `Now previewing as ${opt.key}.` : "Back to your real tier.");
                        setTimeout(() => window.location.reload(), 250);
                      } catch { toast.error("Could not update preview."); }
                      finally { setBusy(false); }
                    }}
                    data-testid={`btn-preview-tier-${opt.key || "clear"}`}
                    className={`px-3 py-1.5 rounded-full text-xs tab-link ${
                      on
                        ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))]"
                        : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
